import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { Table } from '../Common/Table';
import { FilterSelect } from 'utils/FilterSelectUtils';
import Save from './ApplessOccupancySensorForms/save';
import Delete from './ApplessOccupancySensorForms/delete';
import ApplessSensorService, {
  ApplessSensorProps
} from 'services/hooks/appless-occupancy-sensor-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import { generateApiFailErrorMessage } from 'utils/helpers';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';
import SpaceService, { SpaceProps } from 'services/hooks/space-service';

function ApplessSensorsAdminPanel() {
  const [allApplessSensorRecords, setAllApplessSensorRecords] = useState<
    ApplessSensorProps[] | null
  >([]);

  const {
    isLoading,
    refetch,
    data: applessOccupancySensorsData
  } = useQuery<ApplessSensorProps[], Error>(
    'applessOccupancySensors',
    () => ApplessSensorService.findAllApplessOccupancySensors(),
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('App-less occupancy sensors'));
      }
    }
  );

  const { data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  const { data: spacesData } = useQuery<SpaceProps[], Error>('spaces', SpaceService.findAllSpaces, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Spaces'));
    }
  });

  useEffect(() => {
    if (applessOccupancySensorsData && Array.isArray(applessOccupancySensorsData)) {
      setAllApplessSensorRecords(applessOccupancySensorsData);
    }
  }, [applessOccupancySensorsData]);

  const columns = useMemo(
    () => [
      {
        id: 'building',
        Header: 'Building',
        accessor: (row: { buildingUuid: string }) => {
          return buildingsData?.find(
            (buildingData: { uuid?: string }) => buildingData?.uuid === row?.buildingUuid
          )?.name;
        },
        Filter: FilterSelect,
        disableSortBy: false
      },
      {
        id: 'sensorId',
        Header: 'Sensor ID',
        accessor: 'sensorId',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'space',
        Header: 'Space',
        accessor: (row: { spaceUuid: string }) => {
          const space = spacesData?.find(
            (spaceData: { uuid?: string }) => spaceData?.uuid === row?.spaceUuid
          );
          return space ? space.name : 'None';
        },
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'reversed',
        Header: 'Reversed',
        accessor: (row: { reversed: boolean }) => (row.reversed ? 'True' : 'False'),
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'location',
        Header: 'Location',
        accessor: 'location',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'type',
        Header: 'Type',
        accessor: 'type',
        disableFilters: true,
        disableSortBy: false
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: any) => data,
        Cell: (row: any) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    [buildingsData, spacesData]
  );

  if (isLoading || !allApplessSensorRecords) {
    return <FullScreenLoader />;
  }

  return (
    <div className="applessOccupancySensors">
      <Table
        name="Appless Occupancy Sensors"
        refetch={refetch}
        columns={columns}
        data={allApplessSensorRecords}
        defaultSortBy={{ id: 'building', desc: false }}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={ApplessSensorService.createApplessOccupancySensor}
        updateQuery={ApplessSensorService.updateApplessOccupancySensorByID}
        deleteQuery={ApplessSensorService.deleteApplessOccupancySensorByID}
        editQuery={ApplessSensorService.findApplessOccupancySensorByID}
        queryName="applessOccupancySensors"
        buttonName="Sensor"
        toolTipMessage="Install and manage appless occupancy sensors in buildings."
      />
    </div>
  );
}

export default ApplessSensorsAdminPanel;
